import { useEffect, useRef, useState, type ReactNode } from "react";
import { useStats } from "../lib/stats";

/**
 * HomeProofStrip — Rev 25.6 Loop F.
 *
 * 3-stat row between the hero section and the Dynamic Output canvas
 * on the home page. Numbers come from /v1/public/stats (edge-cached
 * 5 min); CountUp animation triggers when the strip scrolls into
 * view (IntersectionObserver, single firing). Reduced-motion users
 * see the final number rendered immediately without animation.
 *
 * Layout: 4-col grid at md+, 2x2 at mobile.
 * Numbers in --nx-text, labels in --nx-text-faint uppercase mono.
 * Border-top + border-bottom in --nx-line separates the strip from
 * the hero above and the Dynamic Output below.
 */

/**
 * Rev 25.6.2 — display floors retired. Both clients and projects are
 * owner-configurable via the admin Settings page; the displayed value
 * is whatever the owner sets in /v1/admin/stats.
 */

function CountUpInt({ to, durationMs = 1200, suffix = "" }: { to: number; durationMs?: number; suffix?: string }) {
  const [shown, setShown] = useState(0);
  const [entered, setEntered] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ref = useRef<HTMLSpanElement | null>(null);
  const rafId = useRef<number | null>(null);
  const shownRef = useRef(0);

  // Rev 26.1 Loop A (revised on prod walk) — animate on first viewport
  // entry AND on every `to` change after entry. The previous attempt
  // used a `ref` for entry tracking, but the IO callback captured `to`
  // at effect-creation time (0); after the fetch resolved and `to`
  // updated to the real value, the second effect's
  // `if (enteredViewport.current) animateTo(to)` would short-circuit
  // because the IO hadn't fired yet, and once the IO eventually fired
  // it called animateTo(stale 0) and disconnected — second effect
  // never re-runs because `[to]` deps hadn't changed since.
  //
  // The fix: track viewport entry in STATE (not ref) so the second
  // effect's deps `[entered, to]` re-fire after first entry. Now the
  // user sees the live value whether scroll comes before or after the
  // fetch resolves.
  //   - mount: state to=0 (fallback), entered=false. No animation.
  //   - fetch resolves: state to=4. entered still false. No animation.
  //   - scroll into view: IO -> setEntered(true). Second effect runs
  //     with (entered=true, to=4). animateTo(4) animates 0->4.
  //   OR
  //   - mount + scroll first (entered=true, to=0). animateTo(0) is a
  //     no-op (from === target). Fetch resolves -> to=4. Second
  //     effect runs. animateTo(4) animates 0->4.
  //   - reduced-motion: snap to `to` synchronously.
  useEffect(() => {
    if (reduced) return;
    if (!ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setEntered(true);
          obs.disconnect();
          return;
        }
      }
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [reduced]);

  useEffect(() => {
    if (reduced) {
      shownRef.current = to;
      setShown(to);
      return;
    }
    if (!entered) return;
    animateTo(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entered, to, reduced]);

  function animateTo(target: number) {
    if (rafId.current != null) cancelAnimationFrame(rafId.current);
    const from = shownRef.current;
    if (from === target) return;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (target - from) * eased);
      shownRef.current = v;
      setShown(v);
      if (t < 1) {
        rafId.current = requestAnimationFrame(tick);
      } else {
        rafId.current = null;
        shownRef.current = target;
        setShown(target);
      }
    };
    rafId.current = requestAnimationFrame(tick);
  }

  return <span ref={ref}>{shown}{suffix}</span>;
}

function Stat({ id, value, label }: { id: string; value: ReactNode; label: string }) {
  return (
    <div className="text-center md:text-left" data-proof-stat={id}>
      <div className="font-display text-3xl font-semibold tracking-tight text-nx-text sm:text-4xl md:text-5xl">
        {value}
      </div>
      <div className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.16em] text-nx-text-faint sm:text-[11px] sm:tracking-[0.18em]">
        {label}
      </div>
    </div>
  );
}

export function HomeProofStrip() {
  const { stats, loaded } = useStats();
  // A zero from the fallback path (API unreachable) must never render
  // as "0+" — swap that slot for an evergreen, always-true fact.
  const live = (n: number) => !loaded || n > 0;
  return (
    <section
      data-home-proof-strip
      aria-label="NEXIRT studio stats"
      className="border-y border-nx-line/60 bg-nx-bg/40"
    >
      <div className="nx-container grid grid-cols-2 gap-x-4 gap-y-8 px-7 py-10 md:grid-cols-4 md:py-12">
        {live(stats.clients) ? (
          <Stat id="clients" value={loaded ? <CountUpInt to={stats.clients} suffix="+" /> : "—"} label="Active clients" />
        ) : (
          <Stat id="delivery" value="6–16 wk" label="Typical delivery" />
        )}
        {live(stats.projects) ? (
          <Stat id="projects" value={loaded ? <CountUpInt to={stats.projects} suffix="+" /> : "—"} label="Systems shipped" />
        ) : (
          <Stat id="stack" value="AWS" label="Serverless-native stack" />
        )}
        <Stat id="uptime" value={stats.uptime} label="Avg uptime maintained" />
        <Stat id="response" value="<1 day" label="Engineer reply time" />
      </div>
    </section>
  );
}

export default HomeProofStrip;
